import { Hourglass01 } from "@untitled-ui/icons-react";
import { range } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";

import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import WorkHistorySidebar from "@apps/work-history/components/work-history-sidebar";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
  ToggleButtonGroupType,
} from "@components/toggle-button-group/toggle-button-group";
import { removeCurrentYear } from "@helpers/helpers";

import ProfileContainer from "./profile-container";

const ProfileWorkHistoryMonth = ({
  profileId,
  monthToSubtract,
  onCompletedFetchingHistory,
}: {
  profileId: number;
  monthToSubtract: number;
  onCompletedFetchingHistory?: () => void;
}) => {
  const showIfDayCountMoreThanXDays = 3;
  const workHistoryStartDate = moment()
    .startOf("month")
    .subtract(monthToSubtract, "months");
  const dayCountFromToday = moment().diff(workHistoryStartDate, "days");
  const workHistoryEndDate = workHistoryStartDate.clone().endOf("month");

  return (
    <ProfileContainer title={workHistoryStartDate.format("MMMM YYYY")}>
      {dayCountFromToday < showIfDayCountMoreThanXDays ? (
        <div className="text-center text-sm text-gray-800 bg-gray-50 rounded-lg px-4 py-8 font-medium">
          <div className="flex justify-center mb-4">
            <Hourglass01 className="w-5 h-5 text-gray-400" />
          </div>
          Your work history will appear on{" "}
          {workHistoryStartDate
            .add(showIfDayCountMoreThanXDays, "days")
            .format("dddd, MMMM D, YYYY")}
        </div>
      ) : (
        <div className="-mt-4">
          <WorkHistorySidebar
            hideSummaryHeader
            userId={profileId}
            startDate={workHistoryStartDate.format("YYYY-MM-DD")}
            endDate={
              moment().isBefore(workHistoryEndDate, "days")
                ? moment().format("YYYY-MM-DD")
                : workHistoryEndDate.format("YYYY-MM-DD")
            }
            sectionClassName="px-0"
            topCollaboratorAvatarSize={5}
            onCompletedFetchingHistory={onCompletedFetchingHistory}
            forceDateFilter={true}
          />
        </div>
      )}
    </ProfileContainer>
  );
};

const ProfileWorkHistoryWeek = ({
  profileId,
  weekToSubtract,
  onCompletedFetchingHistory,
}: {
  profileId: number;
  weekToSubtract: number;
  onCompletedFetchingHistory?: () => void;
}) => {
  const workHistoryStartDate = moment()
    .startOf("isoWeek")
    .subtract(weekToSubtract, "weeks");
  const dayCountFromToday = moment().diff(workHistoryStartDate, "days");
  const showIfDayCountMoreThanXDays = 2;
  const workHistoryEndDate = workHistoryStartDate.clone().endOf("isoWeek");

  return (
    <ProfileContainer
      title={`${removeCurrentYear(
        workHistoryStartDate.format("MMMM D, YYYY"),
      )} - ${removeCurrentYear(workHistoryEndDate.format("MMMM D, YYYY"))}`}
    >
      {dayCountFromToday < showIfDayCountMoreThanXDays ? (
        <div className="text-center text-sm text-gray-800 bg-gray-50 rounded-lg px-4 py-8 font-medium">
          <div className="flex justify-center mb-4">
            <Hourglass01 className="w-5 h-5 text-gray-400" />
          </div>
          Your work history will appear on{" "}
          {workHistoryStartDate
            .add(showIfDayCountMoreThanXDays, "days")
            .format("dddd, MMMM D, YYYY")}
        </div>
      ) : (
        <div className="-mt-4">
          <WorkHistorySidebar
            hideSummaryHeader
            userId={profileId}
            startDate={workHistoryStartDate.format("YYYY-MM-DD")}
            endDate={
              moment().isBefore(workHistoryEndDate, "days")
                ? moment().format("YYYY-MM-DD")
                : workHistoryEndDate.format("YYYY-MM-DD")
            }
            sectionClassName="px-0"
            topCollaboratorAvatarSize={5}
            onCompletedFetchingHistory={onCompletedFetchingHistory}
          />
        </div>
      )}
    </ProfileContainer>
  );
};

const ProfileWorkHistory = ({ profileId }: { profileId: number }) => {
  const defaultPage = 2;
  const organization = currentOrganizationVar();
  const [page, setPage] = useState(defaultPage);
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const [isFetchingHistory, setIsFetchingHistory] = useState(true);

  const handleCompletedFetchingHistory = () => {
    setIsFetchingHistory(false);
  };

  const handleShowMore = () => {
    setIsFetchingHistory(true);
    setPage(page + 1);
  };

  useEffect(() => {
    setPage(defaultPage);
  }, [uiPreferenceCache.profileWorkHistoryRange]);

  if (!organization.featureFlags.workHistory) {
    return null;
  }

  return (
    <div>
      <div className="flex justify-end mb-6">
        <ToggleButtonGroup<"week" | "month">
          theme={ToggleButtonGroupTheme.mergedLightBlueButtons}
          buttons={[
            {
              value: "week",
              title: "Week",
              active: uiPreferenceCache.profileWorkHistoryRange === "week",
            },
            {
              value: "month",
              title: "Month",
              active: uiPreferenceCache.profileWorkHistoryRange === "month",
            },
          ]}
          onClick={(option: ToggleButtonGroupType<"week" | "month">) => {
            saveUiPreference({ profileWorkHistoryRange: option.value });
          }}
        />
      </div>
      <div className="flex flex-col gap-6">
        {range(page).map((pageToSubtract) =>
          uiPreferenceCache.profileWorkHistoryRange === "week" ? (
            <ProfileWorkHistoryWeek
              key={pageToSubtract}
              profileId={profileId}
              weekToSubtract={pageToSubtract}
              onCompletedFetchingHistory={handleCompletedFetchingHistory}
            />
          ) : (
            <ProfileWorkHistoryMonth
              key={pageToSubtract}
              profileId={profileId}
              monthToSubtract={pageToSubtract}
              onCompletedFetchingHistory={handleCompletedFetchingHistory}
            />
          ),
        )}
        <div className="flex justify-center">
          <Button
            theme={buttonTheme.default}
            onClick={handleShowMore}
            text="Show more"
            disabled={isFetchingHistory}
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileWorkHistory;
